import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { MaintenanceOrderSuppliesModel } from 'src/app/models/maintenance/maintenance-order-supplies.model';
import { MaintenanceOrderModel } from 'src/app/models/maintenance/maintenance-order.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MaintenanceOrderSuppliesService } from 'src/app/services/maintenance/maintenance-order-supplies.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-maintenance-order-supplies',
  templateUrl: './register-maintenance-order-supplies.component.html',
  styleUrls: ['./register-maintenance-order-supplies.component.scss'],
  standalone: false
})
export class RegisterMaintenanceOrderSuppliesComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly articleService = inject(ArticleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly maintenanceOrderSuppliesService = inject(MaintenanceOrderSuppliesService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() disabled: boolean = false;
  @Input('maintenanceOrderContract')
  set changeMaintenance(change: MaintenanceOrderModel) {
    this.changeMaintenanceOrder(change);
  }
  @Input('maintenanceOrderSuppliesContract')
  set changeMaintenanceSupplies(change: MaintenanceOrderSuppliesModel) {
    this.changeMaintenanceOrderSupplies(change);
  }
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  codeNameArticle: string = "";
  formMaintenanceOrderSupplies: FormGroup;
  listArticles: any[] = [];
  maintenanceOrderSuppliesId: number = 0;
  maintenanceOrderContract: MaintenanceOrderModel = new MaintenanceOrderModel();
  maintenanceOrderSuppliesContract: MaintenanceOrderSuppliesModel = new MaintenanceOrderSuppliesModel();
  output$ = new Subject<any>();
  subscription: Subscription;


  ngOnInit() {
  }

  changeMaintenanceOrder(maintenanceOrder: MaintenanceOrderModel) {
    if (maintenanceOrder.maintenanceOrderId != 0) {
      this.maintenanceOrderContract = maintenanceOrder;
    }
  }

  changeMaintenanceOrderSupplies(maintenanceOrderSupplies: MaintenanceOrderSuppliesModel) {
    this.maintenanceOrderSuppliesContract = maintenanceOrderSupplies;
    this.maintenanceOrderSuppliesId = maintenanceOrderSupplies.maintenanceOrderSuppliesId;

    this.validateForm(maintenanceOrderSupplies);
    if (this.maintenanceOrderSuppliesId != 0 && this.maintenanceOrderSuppliesContract.mainArticleId != null) {
      this.getListSuppliesForSelect();
    } else {
      if (this.maintenanceOrderSuppliesContract.articleId != 0) {
        this.getArticle(this.maintenanceOrderSuppliesContract.articleId);
      }
    }
  }

  clearForm() {
    this.codeNameArticle = "";
    this.listArticles = [];
    const modelMaintenanceOrderSupplies = new MaintenanceOrderSuppliesModel();
    this.validateForm(modelMaintenanceOrderSupplies)
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null) {
    let validArticle = null;
    let codeNameArticle = "";
    if (articleId != null) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        validArticle = articleId;
        codeNameArticle = `${article.code} - ${article.name}`;
      }
    }

    this.listArticles = [{
      label: codeNameArticle,
      value: validArticle
    }];
    this.formMaintenanceOrderSupplies.controls["articleId"].patchValue(validArticle);
    setTimeout(() => { this.formMaintenanceOrderSupplies.controls["articleId"].disable(); }, 0);
  }

  async getListSuppliesForSelect() {
    try {
      this.listArticles = await firstValueFrom(this.maintenanceOrderSuppliesService.
        getListHomologousArticlesByArticleId(this.maintenanceOrderContract.articleId, this.maintenanceOrderSuppliesContract.mainArticleId)
      );
    } catch (error) {
      console.error('Error al obtener la lista de artículos.', error);
    }
  }

  async send(): Promise<any> {
    if (this.formMaintenanceOrderSupplies.valid) {
      const modelMaintenanceOrderSupplies = this.formMaintenanceOrderSupplies.getRawValue() as MaintenanceOrderSuppliesModel;
      try {
        if (this.maintenanceOrderSuppliesId == 0) {
          // modelMaintenanceOrderSupplies.maintenanceOrderId = this.maintenanceOrderContract.maintenanceOrderId;
          await firstValueFrom(this.maintenanceOrderSuppliesService.createAsync(modelMaintenanceOrderSupplies));
          this.toastHandlerService.showToast('success', 'El insumo fue creado exitosamente.', '');
        }
        else {
          this.maintenanceOrderSuppliesContract.articleId = modelMaintenanceOrderSupplies.articleId;
          this.maintenanceOrderSuppliesContract.quantity = modelMaintenanceOrderSupplies.quantity;
          await firstValueFrom(this.maintenanceOrderSuppliesService.updateMaintenanceOrderSupplies(this.maintenanceOrderSuppliesContract));
          this.toastHandlerService.showToast('success', 'El insumo fue modficado exitosamente.', '');
        }

        this.maintenanceOrderSuppliesContract = new MaintenanceOrderSuppliesModel();
        this.clearForm();
        this.isFormView.emit(true);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al guardar el insumo.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'El campo artículo es obligatorio.'
      },
      quantity: {
        required: 'El campo cantidad es obligatorio.',
        min: 'La cantidad debe ser mayor o igual a 1.',
        max: 'La cantidad debe ser menor o igual a 999.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMaintenanceOrderSupplies.get(controlName);

      if (control && control.invalid && control.errors) {
        const errors = control.errors;

        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(maintenanceOrderSupplies: MaintenanceOrderSuppliesModel) {
    this.formMaintenanceOrderSupplies = this.formBuilder.group({
      maintenanceOrderId: [maintenanceOrderSupplies.maintenanceOrderId, Validators.required],
      articleId: [maintenanceOrderSupplies.articleId, Validators.required],
      quantity: [maintenanceOrderSupplies.quantity, [Validators.required, Validators.min(1), Validators.max(999)]],
    });
  }
}
